import React, { FC, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import Utils from '../utils';
import { CAMPAIGN_PRODUCTS, useFeature } from '../utils/togglz';
import { isInSelectionMode, ProductIdOrOptions } from '../../store/productSelection/actions';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import { PreviewButton } from '../preview/PreviewButton';
import ProductSeries from '../product/ProductSeries';
import ProductAuthors from '../product/ProductAuthors';
import ProductNarrators from '../product/ProductNarrators';
import ProductGenres from '../product/ProductGenres';
import ProductAge from '../product/ProductAge';
import ProductCampaignEndDate from '../product/ProductCampaignEndDate';
import { ProductFormat } from '../product/ProductFormat';
import { ProductReleaseDate } from '../product/ProductReleaseDate';
import ProductStatistics from '../product/ProductStatistics';
import ProductLanguage from '../product/ProductLanguage';
import ProductUsageConditions from '../product/ProductUsageConditions';
import ProductPublisher from '../product/ProductPublisher';
import CampaignUsageBadge from '../../yourborrowbox/marketing/campaigns/CampaignUsageBadge';
import {
    concreteLoanFormatNameToKey,
    concreteLoanFormatNameToObject,
    LOANFORMAT_EPRESS
} from '../../store/loanFormat/actions';
import ProductCampaignLabel from '../product/ProductCampaignLabel';
import ProductCover from './ProductCover';
import { SelectButton } from './SelectButton';
import { TileBadges } from './BadgesV2';
import { AddProduct } from './AddProduct/AddProduct';
import { Article, Price, SubscriptionPeriodType } from '../../../models/Article';
import { ProductMetaData } from '../../../models/Product';
import { ProductSelectionState } from '../../store/productSelection/state';
import { log } from '../utils/log';
import MetadataUtils from '../utils/utils-metadata';
import { ReleaseFrequency } from './ReleaseFrequency';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';

const StDetails = styled.div.attrs({ 'data-displayname': 'ProductTileDetails' })`
    min-height: 105px !important;
`;

const StCampaignUsageBadge = styled(CampaignUsageBadge)`
    margin-bottom: 5px;
`;

export type ProductTileProps = {
    readonly hoverListener?: (hovered: boolean) => void;
    readonly linkToAllLanguagePage?: boolean;
    readonly loanFormat?: string | null;
    readonly showSelectedPriceOnly?: boolean;
    readonly allowPriceSelection?: boolean;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
    readonly onRemoveProductFromCart?: (product: ProductMetaData & Article) => void;
    readonly onSelectPrice: (price: Price, subscriptionPeriodType?: SubscriptionPeriodType) => Promise<void>;
    readonly onProductImageClick?: () => void;
    readonly showSelectionLists?: boolean;
    readonly minimalView?: boolean;
    readonly product: ProductMetaData & Article;
    readonly productSelection: ProductSelectionState | null;
    readonly isDeleteAction?: boolean;
    readonly actionButtons?: React.ReactNode;
    readonly onCreateNewListClicked?: (newSelectionListName: string) => void;
    readonly onSelectProduct?: (props: ProductIdOrOptions) => void;

    /**
     * This is a workaround to enable the ProductSelection for the shop section.
     * Since the ProductTile setup needs its own refactoring, this workaround is ok.
     * But please remove it (build a solution that does not need it), when refactoring the ProductTile setup.
     */
    readonly useOnlyProductIdForSelectButtonCheck?: boolean;

    readonly children?: ReactNode;
};

const onAddPriceFallback = (options: SingleAddOptions) => {
    log.warning(`adding a price was not handled`, options);
    return Promise.resolve();
};

const onSelectPriceFallback = async (price: Price) => {
    log.warning(`selecting a price was not handled`, price);
};

const ProductTile: FC<ProductTileProps> = ({
    product,
    productSelection,
    minimalView: minimalViewSelected = false,
    loanFormat,
    showSelectedPriceOnly,
    allowPriceSelection,
    actionButtons,
    onAddPrice = onAddPriceFallback,
    onSelectPrice = onSelectPriceFallback,
    onProductImageClick,
    onSelectProduct,
    hoverListener,
    useOnlyProductIdForSelectButtonCheck = false,
    children = null
}) => {
    const [isShowDetails, setIsShowDetails] = useState(false);

    const showDetails = () => {
        setIsShowDetails(true);
        hoverListener?.(true);
    };

    const hideDetails = (force = false) => {
        if (!document.getElementById('subscription-start-popup') || force) {
            setIsShowDetails(false);
            hoverListener?.(false);
        }
    };

    const isCampaignProduct = product.productClass === PRODUCT_CLASS_CAMPAIGN;
    const isCampaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const productLoanFormat = concreteLoanFormatNameToObject(product.loanFormat);
    const productLoanFormatKey = productLoanFormat?.key;
    const productLoanFormatSingularName = productLoanFormat?.singularName;
    const shortenedTitle = Utils.truncate(product.title);

    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const loanFormatClass = classNames('prd-tile', minimalViewSelected && 'minimal');

    // hover state
    const tileClasses = classNames('prd-tile-inner', isShowDetails && 'over');

    let productLink = '#';
    if (!isInSelectionMode(productSelection)) {
        productLink = `/products/${product.id}`;
    }

    const minimalView = minimalViewSelected && !isShowDetails;

    let usageConditions: React.ReactNode = <ProductUsageConditions product={product} loanFormatKey={loanFormatKey} />;
    let campaignUsageBadge: React.ReactNode = null;
    let campaignProductLabel: React.ReactNode = null;
    let productCampaignEndDate: React.ReactNode = null;

    if (isCampaignsEnabled && isCampaignProduct) {
        const isPreOrder = MetadataUtils.isProductPreOrderable(product);
        campaignUsageBadge = (
            <StCampaignUsageBadge
                loanFormat={productLoanFormatSingularName!}
                usageLimit={product.price?.usageLimit}
                campaignTerm={product.price?.termLimit}
                isSimultaneousAccess={product.simultaneousAccess}
                releaseDate={product.releaseDate}
                isPreOrder={isPreOrder}
            />
        );
        campaignProductLabel = <ProductCampaignLabel loanFormat={mapLoanFormat(product.loanFormat)!} />;
        if (product.campaignDetails?.salesEndDate) {
            productCampaignEndDate = <ProductCampaignEndDate endDate={product.campaignDetails.salesEndDate} />;
        }
        usageConditions = null;
    }

    return (
        <>
            <div className={loanFormatClass} onMouseLeave={() => hideDetails()}>
                <div className={tileClasses}>
                    <div className="packshot" data-displayname="ProductTile">
                        <Link
                            to={productLink}
                            onClick={e => {
                                if (onProductImageClick) {
                                    e.preventDefault();
                                    onProductImageClick();
                                }
                            }}
                        >
                            <ProductCover onMouseEnter={showDetails} minSizeInPx={300} product={product} />
                        </Link>
                        <SelectButton
                            productOrId={useOnlyProductIdForSelectButtonCheck ? product.id : product}
                            productSelection={productSelection}
                            onClick={onSelectProduct}
                        />
                        <PreviewButton product={product} />
                    </div>

                    <StDetails className="details" data-displayname="ProductTileDetails">
                        {campaignProductLabel}
                        <ProductSeries product={product} onMouseEnter={showDetails} loanFormatKey={loanFormat} />
                        <h3 onMouseEnter={showDetails}>
                            <Link to={productLink}>{shortenedTitle}</Link>
                        </h3>
                        <ProductAuthors product={product} onMouseEnter={showDetails} loanFormatKey={loanFormat} />
                        <div className="details-inner expanded">
                            <ProductNarrators product={product} loanFormatKey={productLoanFormatKey} />
                            <ProductGenres product={product} loanFormatKey={loanFormat} />
                            <ProductAge product={product} loanFormatKey={loanFormat} />

                            <ProductLanguage product={product} label={product.language!} loanFormatKey={loanFormat} />
                            {loanFormatKey === LOANFORMAT_EPRESS ? (
                                <ReleaseFrequency product={product} />
                            ) : (
                                <ProductReleaseDate product={product} />
                            )}
                            {productCampaignEndDate}
                            <ProductPublisher product={product} loanFormatKey={loanFormat} />
                            <ProductFormat product={product} loanFormatKey={productLoanFormatKey!} />
                            {campaignUsageBadge}
                            {usageConditions}
                            {product?.stockDetails ? children : null}
                        </div>
                    </StDetails>

                    <div className="prd-bottom" data-displayname="ProductTileBottom">
                        <TileBadges product={product} loanFormatKey={loanFormat ?? undefined} showAll={isShowDetails} />
                        {minimalView ? null : (
                            <>
                                <ProductStatistics product={product} loanFormatKey={productLoanFormatKey!} />
                                {actionButtons}

                                <AddProduct
                                    product={product}
                                    onAddPrice={onAddPrice}
                                    onSelectPrice={onSelectPrice}
                                    showSelectedPriceOnly={!!showSelectedPriceOnly}
                                    allowSelection={!!allowPriceSelection}
                                    onHideDetails={() => hideDetails(true)}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProductTile;
